import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { mangaList, testimonials } from '@/mocks/manga';

const siteUrl = 'https://hanmanwu.com';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const [selectedChapter, setSelectedChapter] = useState<number>(1);
  const [commentText, setCommentText] = useState('');
  const [comments, setComments] = useState([
    { id: 'c1', user: '漫迷小雨', text: '这一话的剧情太精彩了！画风也一如既往地美，等不及想看下一话了。', time: '2小时前', likes: 128 },
    { id: 'c2', user: '韩漫爱好者', text: '作者对人物心理的刻画太细腻了，每次看都有新的感动。', time: '5小时前', likes: 96 },
    { id: 'c3', user: '追更小能手', text: '剧情越来越精彩了，伏笔埋得太好了！强烈推荐这部漫画。', time: '8小时前', likes: 203 },
  ]);

  const manga = mangaList.find((m) => m.id === id);
  const randomTestimonials = [...testimonials].sort(() => Math.random() - 0.5).slice(0, 2);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (!manga) {
    return (
      <>
        <Navbar />
        <div className="pt-20 flex flex-col items-center justify-center min-h-[60vh]">
          <span className="w-16 h-16 flex items-center justify-center bg-background-100 rounded-full mb-4">
            <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
          </span>
          <p className="text-foreground-500 text-lg">未找到该漫画</p>
          <Link to="/" className="mt-4 text-primary-500 hover:text-primary-600 text-sm font-medium">返回首页</Link>
        </div>
        <Footer />
      </>
    );
  }

  const handleCommentSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    setComments([{ id: `c${Date.now()}`, user: '匿名用户', text: commentText, time: '刚刚', likes: 0 }, ...comments]);
    setCommentText('');
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${manga.title} - 韩漫屋在线阅读`,
    description: manga.description,
    author: { '@type': 'Person', name: manga.author },
    datePublished: '2024-01-15',
    dateModified: manga.updateTime,
    image: manga.coverImage,
    publisher: { '@type': 'Organization', name: '韩漫屋', url: siteUrl },
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${manga.title} | 韩漫屋 - 免费韩国漫画在线阅读`}
        description={`${manga.title} - ${manga.description.substring(0, 100)}。韩漫屋提供${manga.title}免费正版在线阅读，高清画质，每日更新。`}
        keywords={`${manga.title},韩漫屋,韩国漫画,免费韩漫,${manga.category}漫画,${manga.tags.join(',')}`}
        canonicalUrl={`${siteUrl}/article/${manga.id}`}
        ogType="article"
        ogImage={manga.coverImage}
      />

      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <main>
        <Navbar />
        <div className="pt-20">
          <div className="w-full px-4 md:px-6">
            {/* Breadcrumb */}
            <div className="py-4">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: `${manga.category}漫画`, href: `/category/${manga.categorySlug}` },
                  { label: manga.title },
                ]}
              />
            </div>

            {/* Article Header */}
            <div className="flex flex-col md:flex-row gap-6 md:gap-8 mb-10">
              <div className="flex-shrink-0">
                <div className="w-40 md:w-48 aspect-[3/4] rounded-xl overflow-hidden bg-background-200">
                  <img src={manga.coverImage} alt={manga.title} title={`${manga.title}封面`} className="w-full h-full object-cover object-top" />
                </div>
              </div>
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                  {manga.title}
                </h1>
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="text-sm text-foreground-500">作者：{manga.author}</span>
                  <span className="text-sm text-foreground-500">{manga.views} 阅读</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500 text-sm"></i>
                    <span className="text-sm text-foreground-700 font-medium">{manga.rating}</span>
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${
                    manga.status === '连载中' ? 'bg-primary-100 text-primary-700' : 'bg-foreground-200 text-foreground-700'
                  }`}>
                    {manga.status}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {manga.tags.map((tag) => (
                    <span key={tag} className="text-xs px-2 py-1 bg-secondary-100 text-secondary-700 rounded-md whitespace-nowrap">{tag}</span>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-6">{manga.description}</p>
                <div className="flex items-center gap-3">
                  <Link
                    to={`/article/${manga.id}`}
                    className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-5 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-play-circle-line"></i>
                    开始阅读
                  </Link>
                  <button className="inline-flex items-center gap-2 bg-background-100 hover:bg-background-200 text-foreground-700 px-5 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-heart-line"></i>
                    收藏
                  </button>
                </div>
              </div>
            </div>

            {/* Chapter List */}
            <div className="mb-12">
              <h2 className="text-lg font-bold text-foreground-900 mb-4">章节列表（共 {manga.chapters.length} 话）</h2>

              {/* Selected Chapter Content Preview */}
              <div className="bg-background-100 rounded-xl p-6 md:p-8 mb-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-base font-semibold text-foreground-900">
                    {manga.chapters[selectedChapter - 1]?.title || `第${selectedChapter}话`}
                  </h3>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setSelectedChapter(Math.max(1, selectedChapter - 1))}
                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-50 text-foreground-600 hover:bg-primary-100 hover:text-primary-500 transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-left-s-line"></i>
                    </button>
                    <span className="text-sm text-foreground-500">{selectedChapter} / {manga.chapters.length}</span>
                    <button
                      onClick={() => setSelectedChapter(Math.min(manga.chapters.length, selectedChapter + 1))}
                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-50 text-foreground-600 hover:bg-primary-100 hover:text-primary-500 transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-right-s-line"></i>
                    </button>
                  </div>
                </div>

                {/* Mock reader content */}
                <div className="space-y-4">
                  <div className="w-full h-64 md:h-96 bg-background-200 rounded-lg flex items-center justify-center overflow-hidden">
                    <img
                      src={manga.coverImage}
                      alt={`${manga.title} - ${manga.chapters[selectedChapter - 1]?.title || `第${selectedChapter}话`}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <p className="text-sm text-foreground-600 text-center leading-relaxed px-4">
                    <strong>{manga.chapters[selectedChapter - 1]?.title || `第${selectedChapter}话`}</strong>
                    <br />
                    更新于 {manga.chapters[selectedChapter - 1]?.updateTime || manga.updateTime}
                  </p>
                </div>
              </div>

              {/* Chapter Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                {manga.chapters.map((ch) => (
                  <button
                    key={ch.id}
                    onClick={() => setSelectedChapter(ch.number)}
                    className={`text-left px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer ${
                      selectedChapter === ch.number
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-700 hover:bg-primary-100'
                    }`}
                  >
                    <div className="truncate font-medium">{ch.title}</div>
                    <div className={`text-xs mt-0.5 ${selectedChapter === ch.number ? 'text-background-50/80' : 'text-foreground-400'}`}>{ch.updateTime}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Comments Section */}
            <div className="mb-12">
              <h2 className="text-lg font-bold text-foreground-900 mb-4">读者评论（{comments.length}）</h2>

              <form onSubmit={handleCommentSubmit} className="mb-6">
                <textarea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  maxLength={500}
                  placeholder="写下你对这部漫画的看法..."
                  className="w-full bg-background-100 border border-background-200/70 rounded-xl px-4 py-3 text-sm text-foreground-900 placeholder-foreground-400 resize-none outline-none focus:border-primary-300 transition-colors"
                  rows={3}
                />
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-foreground-400">{commentText.length}/500</span>
                  <button
                    type="submit"
                    className="bg-primary-500 hover:bg-primary-600 text-background-50 px-5 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
                  >
                    发表评论
                  </button>
                </div>
              </form>

              <div className="space-y-4">
                {comments.map((comment) => (
                  <div key={comment.id} className="bg-background-100 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="w-8 h-8 flex items-center justify-center bg-primary-100 rounded-full text-xs font-bold text-primary-600">
                          {comment.user.charAt(0)}
                        </span>
                        <span className="text-sm font-medium text-foreground-900">{comment.user}</span>
                      </div>
                      <span className="text-xs text-foreground-400">{comment.time}</span>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-2">{comment.text}</p>
                    <div className="flex items-center gap-4">
                      <button className="flex items-center gap-1 text-xs text-foreground-400 hover:text-primary-500 transition-colors cursor-pointer">
                        <i className="ri-thumb-up-line"></i>
                        {comment.likes}
                      </button>
                      <button className="text-xs text-foreground-400 hover:text-primary-500 transition-colors cursor-pointer">回复</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Related Manga */}
            <div className="mb-12">
              <h2 className="text-lg font-bold text-foreground-900 mb-4">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {mangaList
                  .filter((m) => m.id !== manga.id && (m.categorySlug === manga.categorySlug || m.tags.some((t) => manga.tags.includes(t))))
                  .slice(0, 6)
                  .map((m) => (
                    <Link key={m.id} to={`/article/${m.id}`} className="group flex flex-col">
                      <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200 mb-3">
                        <img src={m.coverImage} alt={m.title} className="w-full h-full object-cover object-top" />
                      </div>
                      <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">{m.title}</h3>
                      <p className="text-xs text-foreground-500 mt-1">{m.views} 阅读</p>
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </main>
    </>
  );
}